import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import { ComponentType, forwardRef, PropsWithChildren } from 'react';

import { AbgTypography, ArrowDownIcon, ClockIcon } from '@abg/kit-base';

import { DateTimePickerErrorEnum } from '../types';

import { DateTimePickerErrorBanner } from './DateTimePickerErrorBanner';
import {
  accordionStyles,
  accordionSummaryStyles,
  errorBannerStyles,
  iconStyles,
  subtitleStyles,
  titleStyles,
  titleWrapperStyles,
} from './PickerAccordion.styling';

type PickerAccordionProps = PropsWithChildren & {
  isExpanded: boolean;
  label: string | undefined;
  title: string;
  subtitle?: string;
  toggleAccordion: (event: React.SyntheticEvent, expanded: boolean) => void;
  IconComponent?: ComponentType;
  showDateTimePickerError: DateTimePickerErrorEnum | boolean;
  maxBookingPeriod: number;
};

export const PickerAccordion = forwardRef<HTMLDivElement, PickerAccordionProps>(
  (
    {
      children,
      isExpanded,
      label,
      title,
      subtitle,
      toggleAccordion,
      IconComponent = ClockIcon,
      showDateTimePickerError,
      maxBookingPeriod,
    },
    reference,
  ) => {
    return (
      <Accordion
        expanded={isExpanded}
        onChange={toggleAccordion}
        sx={accordionStyles}
        ref={reference}
        data-testid="picker-accordion"
      >
        <AccordionSummary
          sx={accordionSummaryStyles}
          expandIcon={<ArrowDownIcon sx={iconStyles(isExpanded)} />}
          data-testid="picker-accordion-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconComponent sx={iconStyles(isExpanded)} />
            <Box sx={titleWrapperStyles}>
              <AbgTypography
                sx={titleStyles(isExpanded)}
                variant="bodySmallMedium"
                data-testid="picker-accordion-title"
              >
                {title}
              </AbgTypography>
              {subtitle && (
                <AbgTypography
                  sx={subtitleStyles(isExpanded)}
                  variant="caption"
                  data-testid="picker-accordion-subtitle"
                >
                  {subtitle}
                </AbgTypography>
              )}
            </Box>
          </Box>
          {label && (
            <AbgTypography
              sx={titleStyles(isExpanded)}
              variant="bodySmallMedium"
              data-testid="picker-accordion-label"
            >
              {label}
            </AbgTypography>
          )}
        </AccordionSummary>
        <AccordionDetails>
          {children}

          {isExpanded && showDateTimePickerError && (
            <DateTimePickerErrorBanner
              showDateTimePickerError={
                showDateTimePickerError as DateTimePickerErrorEnum
              }
              maxBookingPeriod={maxBookingPeriod}
              sxOverrides={errorBannerStyles}
            />
          )}
        </AccordionDetails>
      </Accordion>
    );
  },
);

PickerAccordion.displayName = 'PickerAccordion';
